import {
  ACTIVATION_CODE_ALPHABET,
  ACTIVATION_CODE_GROUP_SIZE,
  ACTIVATION_CODE_LENGTH,
} from '../constants';

const SEPARATORS = /[\s\-\u2010-\u2015\u2212_]/gu;
const VALID_NORMALIZED = new RegExp(`^[${ACTIVATION_CODE_ALPHABET}]{${ACTIVATION_CODE_LENGTH}}$`);

/**
 * §10 normalisation: trim, uppercase, remove whitespace and dashes (incl. typographic dashes
 * that phones insert on paste). `k7pf2 9mqdx`, `K7PF2-9MQDX`, `K7PF29MQDX` → `K7PF29MQDX`.
 * Does not validate – use `isValidActivationCodeSyntax`.
 */
export function normalizeActivationCode(input: string): string {
  return input.normalize('NFKC').trim().toUpperCase().replace(SEPARATORS, '');
}

/** True if the input normalises to exactly 10 characters of the reduced alphabet. */
export function isValidActivationCodeSyntax(input: string): boolean {
  return VALID_NORMALIZED.test(normalizeActivationCode(input));
}

/**
 * Display format `XXXXX-XXXXX`. Accepts raw or normalised input.
 * @throws Error if the input is not a syntactically valid activation code.
 */
export function formatActivationCode(input: string): string {
  const normalized = normalizeActivationCode(input);
  if (!VALID_NORMALIZED.test(normalized)) {
    throw new Error('Invalid activation code syntax');
  }
  return `${normalized.slice(0, ACTIVATION_CODE_GROUP_SIZE)}-${normalized.slice(ACTIVATION_CODE_GROUP_SIZE)}`;
}

/**
 * Formatting while the user types: normalises, drops characters outside the alphabet,
 * truncates to 10 characters and inserts the dash after the 5th character.
 * `k7pf29` → `K7PF2-9`.
 */
export function formatActivationCodeInput(input: string): string {
  const cleaned = Array.from(normalizeActivationCode(input))
    .filter((char) => ACTIVATION_CODE_ALPHABET.includes(char))
    .slice(0, ACTIVATION_CODE_LENGTH)
    .join('');
  return cleaned.length > ACTIVATION_CODE_GROUP_SIZE
    ? `${cleaned.slice(0, ACTIVATION_CODE_GROUP_SIZE)}-${cleaned.slice(ACTIVATION_CODE_GROUP_SIZE)}`
    : cleaned;
}

/**
 * Masked display for admin tables (§39 „Code anzeigen“ is a separate, audited action):
 * only the last 4 characters are visible, e.g. `•••••-•MQDX`.
 */
export function maskActivationCode(input: string): string {
  const formatted = formatActivationCode(input);
  const visibleFrom = formatted.length - 4;
  return Array.from(formatted)
    .map((char, i) => (char === '-' || i >= visibleFrom ? char : '•'))
    .join('');
}

/**
 * §42: the QR code contains exactly this URL and nothing else.
 * `buildActivationUrl('https://zugang.example.de', 'k7pf29mqdx')` → `https://zugang.example.de/a/K7PF2-9MQDX`.
 */
export function buildActivationUrl(publicBaseUrl: string, code: string): string {
  return `${publicBaseUrl.replace(/\/+$/u, '')}/a/${formatActivationCode(code)}`;
}
